/**
 * Help Center: "travel-insurance" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'does-defera-offer-travel-insurance',
    title: 'Does Defera offer travel insurance?',
    summary: 'Yes. On eligible trips, you can add optional travel insurance from Faye at checkout, or later from your trip page until {{INSURANCE_CUTOFF_DAYS}} days before departure.',
    keywords: ['trip insurance', 'Faye', 'trip protection', 'cancellation insurance', 'travel coverage', 'insure my trip', 'Faye First', 'Faye Forward'],
    related: ['travel-insurance/add-insurance-at-checkout', 'travel-insurance/add-insurance-after-booking', 'travel-insurance/insurance-and-flight-changes', 'getting-started/price-protection'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `Defera offers optional travel insurance from Faye on eligible trips. Here's our full answer, and below it, a closer look at each part.

{{TRAVEL_INSURANCE_ANSWER}}

## Can I add it to my trip?

Travel insurance can be added to trips that are:

- on a Defera Installments plan
- a round trip, with a base fare of {{INSURANCE_MIN_FARE}} or more
- departing at least {{INSURANCE_CUTOFF_DAYS}} days out

It isn't available with Defera Zero or on one-way trips. It also can't be combined with Defera Miles: a trip that uses Miles can't add travel insurance, and a trip with travel insurance can't use Miles.

## How do I add it?

There are two ways:

- **At checkout.** On eligible trips, the checkout page offers travel insurance from Faye as an optional add-on.
- **Later, from your trip page.** If you didn't add it when you booked, an eligible trip shows a travel insurance card on your trip page until {{INSURANCE_CUTOFF_DAYS}} days before departure.

[Adding travel insurance after you book](/help/travel-insurance/add-insurance-after-booking) walks through the second option.

## What does it cost?

You pay exactly Faye's price for the plan. Defera adds no fees to it, and no discounts apply.

How it's paid depends on when you add it:

- **At checkout:** it's included in your trip total and spread across your payments after the deposit, so your deposit doesn't change.
- **Later, with payments still to come:** it's added to your trip total and spread across your remaining payments.
- **Later, on a trip that's already paid off:** it's charged once to your card on file, the card your trip's payments are charged to.

[Adding travel insurance at checkout](/help/travel-insurance/add-insurance-at-checkout) has more on the price and the plans.

## What if I change or cancel my trip?

- **If you change your flight,** your travel insurance is re-priced for the new flight. An increase is added to what you pay today for the change, and a decrease goes back to your card.
- **If you cancel,** the cancel screen shows what happens to the travel insurance part. Where it's refunded, it goes back to your card, not to Defera Credit.

[Travel insurance when you cancel](/help/travel-insurance/insurance-and-cancellations) explains more.

## Who answers coverage questions and handles claims?

Faye does. Defera offers Faye's plans as a travel retailer and isn't a licensed insurance agent. We can describe the plans and their price, but questions about what a plan covers, and all claims, go to Faye. [Travel insurance coverage questions and claims](/help/travel-insurance/insurance-claims) has Faye's contact details.

## Is this the same as price protection?

No. Travel insurance is separate from your trip-price protection, and it's separate from Defera Credit too. [How your trip price is protected](/help/getting-started/price-protection) explains price protection.`,
  },
  {
    slug: 'add-insurance-at-checkout',
    title: 'Adding travel insurance at checkout',
    summary: 'On eligible trips, checkout offers optional travel insurance from Faye. Its price joins your trip total and is spread across your payments after the deposit.',
    keywords: ['buy travel insurance', 'Faye First', 'Faye Forward', 'insurance price', 'insurance cost', 'eligible trips', 'review page', 'add-on'],
    related: ['travel-insurance/does-defera-offer-travel-insurance', 'travel-insurance/add-insurance-after-booking', 'booking/checkout-page', 'travel-insurance/insurance-claims'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `On eligible trips, the checkout page offers travel insurance from Faye as an optional add-on. If you add it, its price is included in your trip total and spread across your payments after the deposit, so your deposit stays the same.

You pay exactly Faye's price for the plan, with no Defera fees added. And it's entirely your choice: travel insurance is never required to book with Defera.

## Which trips can add it?

Travel insurance is offered at checkout on trips that are:

- on a Defera Installments plan
- a round trip, with a base fare of {{INSURANCE_MIN_FARE}} or more
- departing at least {{INSURANCE_CUTOFF_DAYS}} days out

At checkout, it can only be added to a plan that has payments after the deposit.

It isn't available with Defera Zero, on one-way trips, or on bookings that use Defera Miles, including a trip booked with Miles alone. A trip that uses Miles can't add travel insurance, and a trip with travel insurance can't use Miles.

If your trip seems to fit these rules and you don't see the offer, ask the [Defera Concierge](/support), our AI concierge. It can send a request to our team to take a look.

## How to add it

Here's what happens:

1. **Find the offer.** On the checkout page, look for the travel insurance offer from Faye. It appears only on eligible trips.
2. **Review the plans.** Look over the plans offered and their prices.
3. **Choose a plan, or leave it off.** It's optional, and you can book without it.
4. **Check your trip total and payment schedule.** If you added a plan, its price is part of your trip total, spread across the payments after your deposit.
5. **Finish booking.** You'll get an email with the plan you added and how it's paid.

On eligible trips, the short list of questions under the payment schedule at checkout also includes one about travel insurance.

## Which plans are offered?

Two plans are offered:

- **Faye First,** for trips within the 50 states and DC. For trips that leave the 50 states and DC, it's Faye First International, at the same price.
- **Faye Forward,** one plan for trips at home and abroad.

Questions about what a plan covers go to Faye. [Travel insurance coverage questions and claims](/help/travel-insurance/insurance-claims) has their contact details.

## What does it cost, and how is it paid?

- **Faye's price, exactly.** Defera adds no fees to it: no service fee and no hold fee. No discounts, promotions or Defera Miles apply, and Miles aren't earned on it.
- **Part of your trip total.** It's included in the trip total shown at checkout, not a separate or later charge.
- **Spread across your payments after the deposit.** Your deposit is the same with or without it.

For example, say your plan has a deposit and then six payments. If you add travel insurance, its price is spread across those six payments, and your deposit stays exactly what it would have been without it.

## What am I agreeing to?

When you add travel insurance, the acknowledgments at checkout set the travel insurance part apart from the cancellation and default rules.

In plain English: the travel insurance part of what you pay follows its own section of our [Terms of Service](/terms), called "Travel Insurance by Faye", rather than the usual cancellation and default rules.

## Who answers coverage questions?

Defera offers Faye's plans as a travel retailer and isn't a licensed insurance agent. So we can describe the plans and their price, but questions about coverage, and all claims, go to Faye.`,
  },
  {
    slug: 'add-insurance-after-booking',
    title: 'Adding travel insurance after you book',
    summary: 'Didn’t add it at checkout? Eligible trips show a travel insurance card on the trip page until {{INSURANCE_CUTOFF_DAYS}} days before departure, with the same plans and price rules.',
    keywords: ['add insurance later', 'insurance after booking', 'trip page', 'Faye', 'insurance card', 'paid off', 'date of birth', 'remaining payments'],
    related: ['travel-insurance/add-insurance-at-checkout', 'travel-insurance/does-defera-offer-travel-insurance', 'missed-payments/past-due-balance', 'travel-insurance/insurance-claims'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `If you didn't add travel insurance at checkout, you may be able to add it later from your trip page, until {{INSURANCE_CUTOFF_DAYS}} days before departure. A trip that qualifies shows an optional travel insurance card from Faye, with the same plans and price rules as at checkout.

You'll see exactly how it changes your payments before you accept anything. If payments are still to come, the price is spread across them. If your trip is already paid off, it's a one-time charge to your card on file, which is the card your trip's payments are charged to.

## Can my trip add it?

The same rules as at checkout apply. Your trip needs to be:

- on a Defera Installments plan
- a round trip, with a base fare of {{INSURANCE_MIN_FARE}} or more
- at least {{INSURANCE_CUTOFF_DAYS}} days before departure

A few more things to know:

- **Some trips can't add it.** It isn't offered with Defera Zero, on bookings that used Defera Miles, on some older Defera Installments plans, or on one-way trips.
- **Dates of birth are needed.** Every traveler on the trip needs a date of birth on file.
- **A trip carries one plan.** Once it has travel insurance, it can't be added again.

## How to add it

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Find the travel insurance card.** It's on your trip page when your trip qualifies.
3. **Review the plans.** Look over the plans offered and their prices.
4. **Check what changes.** Before you accept, your trip page shows your new payment amounts, or the one-time charge if your trip is paid off.
5. **Accept the terms to add the plan.** You'll get an email with the plan you added and how it's paid.

## How is it paid?

It depends on whether your trip still has payments to come:

| Your trip | How travel insurance is paid |
|---|---|
| Payments still to come | Added to your trip total and spread across your remaining payments, on the same dates. Payments you've already made don't change. |
| Already paid off | Charged once, that day, to your card on file. It isn't part of a payment plan. |

Either way, you pay exactly Faye's price, with no Defera fees added and no discounts or Defera Miles applied.

For example, if your trip has four payments left when you add a plan, its price is spread across those four payments, and they keep the same dates. The payments you've already made stay as they were.

## Why don't I see the card?

The offer waits while:

- **A payment on your trip is past due or being retried.** Pay that first from **Make a Payment**. [Catching up on a past-due balance](/help/missed-payments/past-due-balance) explains how.
- **Your payment plan is paused.** The offer waits until your plan is running again.
- **Your next payment is going through.** This takes a day or so.

Once nothing is holding it up, the card can appear, as long as your trip still fits the rules above.

## Still don't see it?

If your trip fits the rules above and you still don't see the card, ask the [Defera Concierge](/support), our AI concierge. It can send a request to our team to take a look.

## What if it couldn't be confirmed?

If adding travel insurance ends with "we couldn't confirm this just now", or your trip page says an earlier request is still being confirmed, the connection dropped while the plan was being added. Our team is checking it.

- You won't be charged twice.
- Please don't try again until it clears. Your trip page shows the plan once it's confirmed.
- If it's still showing after a few hours, [contact us](/contact).`,
  },
  {
    slug: 'insurance-and-flight-changes',
    title: 'Travel insurance when you change your flight',
    summary: 'Changing your flight re-prices your travel insurance for the new flight. An increase is added to what you pay today; a decrease goes back to your card.',
    keywords: ['re-priced', 'new dates', 'insurance refund', 'insurance cost went up', 'reschedule', 'date change', 'Faye First International'],
    related: ['changes-cancellations/change-your-flight', 'travel-insurance/insurance-and-cancellations', 'payments/refund-timing', 'travel-insurance/insurance-claims'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `When you change the flight on a trip with travel insurance, the insurance is re-priced for your new flight. You'll see the new price on the review screen before you confirm the change.

If the price goes up, the increase is added to what you pay today for the change. If it goes down, the difference is refunded to your card. Either way, your payment plan doesn't change.

## Why does the price change?

Faye prices its plans on the trip's cost and length. When you change your flight, those can change too, so your plan is re-priced for the new flight.

## What happens to the price?

There are two possibilities:

| If the insurance price | What happens |
|---|---|
| Goes up | The increase is added to what you pay today for the change. |
| Goes down | The difference is refunded to your card. It doesn't become Defera Credit. |

Whichever way it goes, you'll see the new price before you confirm, and your payment plan stays the same.

## When does a refund reach my card?

A refund to your card leaves Defera right away, but your bank decides when it appears. That's typically within 5–10 business days, and sometimes sooner. It goes back to the card you paid with. [When will my refund arrive?](/help/payments/refund-timing) has more.

## How do I change my flight?

On Defera, a flight change means new dates or times on the same route. You start one from **Flight Options** on your trip, and the review screen shows the re-priced travel insurance before you confirm. [How to change your flight](/help/changes-cancellations/change-your-flight) walks you through it.

## What if my new flights leave the 50 states and DC?

Faye First becomes Faye First International, which is priced the same as Faye First.

## What if I cancel instead?

The cancel screen shows what happens to the travel insurance part of what you've paid. Where it's refunded, it goes back to your card, not to Defera Credit. [Travel insurance when you cancel](/help/travel-insurance/insurance-and-cancellations) explains more.

## Questions about your coverage

Questions about what your plan covers after the change go to Faye. [Travel insurance coverage questions and claims](/help/travel-insurance/insurance-claims) has their contact details.`,
  },
  {
    slug: 'insurance-and-cancellations',
    title: 'Travel insurance when you cancel',
    summary: 'The cancel screen shows what happens to the travel insurance part of what you’ve paid. Where it’s refunded, it goes back to your card, not to Defera Credit.',
    keywords: ['cancel insurance', 'insurance refund', 'cancel trip with insurance', 'Defera Credit', 'remove insurance', 'refund to card', 'Faye'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/what-you-get-back', 'payments/refund-timing', 'travel-insurance/insurance-claims'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `If you cancel a trip that includes travel insurance, the cancel screen shows what happens to the travel insurance part of what you've paid. Where that part is refunded, it goes back to the card you paid with, never into Defera Credit.

That's true even when everything else on your trip comes back as Defera Credit. We know cancelling a trip is a big decision, so here's how the insurance part fits in.

## Where can I see it?

Here's how:

1. **Open your trip.** Go to **Trips** and tap your trip.
2. **Tap Cancel.** Tap **Flight Options**, then **Cancel**.
3. **Review your options.** The cancel screen shows your options, including what happens to the travel insurance part.

## How does it fit with Defera Credit?

Defera Credit is credit on your Defera account. It never expires, and you can use it toward any future booking on Defera, for yourself or for someone else.

If your cancel option returns what you've paid as Defera Credit, that covers everything except the travel insurance part. The insurance part is handled on its own: where it's refunded, it goes back to your card.

For example, if the cancel screen offers 100% of what you've paid back as Defera Credit, that means 100% of everything except the travel insurance part. The cancel screen shows what happens to that part separately.

Defera Credit still can't be turned into cash. The insurance part never becomes credit in the first place, so that rule doesn't change.

## When does a card refund arrive?

Card refunds leave Defera right away, but your bank decides when they appear. That's typically within 5–10 business days, and sometimes sooner. [When will my refund arrive?](/help/payments/refund-timing) has more.

## Can I remove travel insurance?

There's no self-serve way to remove travel insurance from a trip, whether or not you're cancelling the trip.

To ask, chat with the [Defera Concierge](/support), our AI concierge, which can send a request to our team. The request shows on your trip page, and the answer appears there and comes to you by email.

## What do the terms say?

When you added travel insurance, the acknowledgments or terms you accepted set the travel insurance part apart from the cancellation and default rules. In plain English: the travel insurance part follows its own section of our [Terms of Service](/terms), called "Travel Insurance by Faye", rather than the usual cancellation and default rules.

If your trip is in payment default, contact us about the travel insurance part, and our team reviews it.

## Is my reason for cancelling covered?

Whether your reason for cancelling is covered by your plan is a question for Faye, and so are claims. [Travel insurance coverage questions and claims](/help/travel-insurance/insurance-claims) has Faye's contact details.`,
  },
  {
    slug: 'insurance-claims',
    title: 'Travel insurance coverage questions and claims',
    summary: 'Faye answers coverage questions and handles claims, filed in the Faye app. Reach Faye at support@withfaye.com, +1-833-240-7056 (US) or +1-804-482-2122 (abroad).',
    keywords: ['file a claim', 'Faye app', 'what does it cover', 'policy number', 'pre-existing condition', 'Faye phone number', 'Faye email', 'am I covered'],
    related: ['travel-insurance/does-defera-offer-travel-insurance', 'travel-insurance/insurance-and-cancellations', 'travel-insurance/add-insurance-at-checkout', 'contact/how-to-contact'],
    updated: '2026-10-05',
    flag: 'faye',
    body: `Faye answers questions about what your travel insurance covers, and it handles all claims. You file a claim with Faye, in the Faye app.

Defera offers Faye's plans as a travel retailer and isn't a licensed insurance agent. That means we can describe the plans and their price, but we can't say whether a particular situation would be covered. Faye can give you a real answer.

## How do I reach Faye?

- **Email:** [support@withfaye.com](mailto:support@withfaye.com)
- **Phone from the US:** [+1-833-240-7056](tel:+18332407056)
- **Phone from outside the US:** [+1-804-482-2122](tel:+18044822122)
- **Website:** [withfaye.com](https://withfaye.com)

Claims are filed with Faye, in the Faye app.

## What should I ask Faye?

Faye is the right place for questions like these:

- whether a particular situation, medical condition, trip, delay or claim would be covered
- pre-existing conditions
- what the terms in your plan mean
- when your coverage starts, and what it covers
- how a claim will be decided
- questions specific to the state you live in, since Faye's plans, coverage and terms can vary by state of residence

## What about insurance I already have?

If you have questions about insurance you already have, such as health, home or auto coverage, contact that insurer, or your insurance agent or broker.

## What can Defera help with?

We're glad to help with the parts that are about your Defera trip:

- adding travel insurance, and how its price fits into your payments
- your payment schedule and your card on file
- what happens to the travel insurance part when you change your flight or cancel your trip

For any of these, or if you're not sure whether a question is for us or for Faye, [chat with the Defera Concierge](/support), our AI concierge. For coverage questions and claims, it gives you Faye's contact details.

## Where's my policy number?

Faye issues your policy before you travel, and your policy number appears on your trip page once your trip is paid in full. Until then, your trip page shows the plan as included, which is normal.

If you hear from Faye directly, for example with policy documents, questions about them go to Faye.`,
  },
];
